import React, { useState } from 'react';
import { Plus, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { useJobs } from '../../core/hooks/useJobs';
import { useAuth } from '../../core/contexts/AuthContext';
import { JobDialog } from './components/JobDialog';
import { JobStatus, JobPriority } from '../../core/models/Job';
import { ColumnDef } from '@tanstack/react-table';
import type { Job } from '../../core/models/Job';
import { Timestamp } from 'firebase/firestore';

export const JobsPage: React.FC = () => {
  const { organizationId } = useAuth();
  const { jobs, loading, error, createJob, updateJob } = useJobs();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  const handleCreateJob = async (data: any) => {
    try {
      if (!organizationId) {
        alert('Client ID is required. Please ensure you are logged in with a valid client context.');
        return;
      }
      if (!data.contractId) {
        alert('Contract is required to create a job.');
        return;
      }
      await createJob({
        ...data,
        organizationId,
        contractId: data.contractId,
        dueDate: data.dueDate ? Timestamp.fromDate(new Date(data.dueDate)) : undefined,
        status: data.status || JobStatus.DRAFT,
        priority: data.priority || JobPriority.MEDIUM,
      });
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to create job:', err);
      const errorMessage = err?.message || 'Failed to create job. Please try again.';
      alert(errorMessage);
    }
  };

  const handleUpdateJob = async (data: any) => {
    if (!editingJob) return;
    try {
      await updateJob(editingJob.id, {
        ...data,
        dueDate: data.dueDate ? Timestamp.fromDate(new Date(data.dueDate)) : undefined,
      });
      setEditingJob(null);
    } catch (err: any) {
      console.error('Failed to update job:', err);
      const errorMessage = err?.message || 'Failed to update job. Please try again.';
      alert(errorMessage);
    }
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return 'success';
      case JobStatus.IN_PROGRESS:
        return 'default';
      case JobStatus.ON_HOLD:
        return 'warning';
      case JobStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: JobPriority) => {
    switch (priority) {
      case JobPriority.URGENT:
        return 'error';
      case JobPriority.HIGH:
        return 'warning';
      case JobPriority.MEDIUM:
        return 'default';
      case JobPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: ColumnDef<Job>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.title}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getStatusColor(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge variant={getPriorityColor(row.original.priority)}>
          {row.original.priority}
        </Badge>
      ),
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }) =>
        row.original.dueDate
          ? new Date(row.original.dueDate).toLocaleDateString()
          : 'N/A',
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditingJob(row.original)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (loading) {
    return <LoadingState message="Loading jobs..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load jobs" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Jobs"
        description="Manage and track your jobs"
        icon={Briefcase}
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Job
          </Button>
        }
      />

      {jobs.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No jobs found"
          description="Create your first job to get started"
          action={{
            label: 'Create Job',
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      ) : (
        <DataTable columns={columns} data={jobs} searchKey="title" searchPlaceholder="Search jobs..." />
      )}

      {/* Create Job Dialog */}
      <JobDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateJob}
      />

      {/* Edit Job Dialog */}
      {editingJob && (
        <JobDialog
          open={!!editingJob}
          onClose={() => setEditingJob(null)}
          job={editingJob}
          onSubmit={handleUpdateJob}
        />
      )}
    </div>
  );
};
